import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Pencil, Shield, ShieldCheck, RefreshCw, Power, Trash2, Key, Clock, Server as ServerIcon, Loader2, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { Server } from '@/types/server';
import { cn } from '@/lib/utils';
import {
  fetchServerMetrics,
  fetchServerMetricsHistory,
  type ServerMetricsResponse,
  type ServerMetricsHistoryResponse,
} from '@/api/servers';
import { toast } from 'sonner';

interface ServerCardProps {
  server: Server;
  projectId: string;
  onUpdateDisplayName: (serverId: number, name: string) => Promise<void>;
  onToggleSecurityUpdates: (serverId: number, enabled: boolean) => Promise<void>;
  onReboot: (serverId: number) => Promise<void>;
  onForceReboot: (serverId: number) => Promise<void>;
  onDestroy: (serverId: number) => Promise<void>;
  onAddSSHKey: (serverId: number, key: string) => Promise<void>;
}

export function ServerCard({
  server,
  projectId: _projectId,
  onUpdateDisplayName,
  onToggleSecurityUpdates,
  onReboot,
  onForceReboot,
  onDestroy,
  onAddSSHKey,
}: ServerCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(server.display_name);
  const [isUpdating, setIsUpdating] = useState(false);
  const [sshKey, setSSHKey] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [sshDialogOpen, setSSHDialogOpen] = useState(false);
  const [rebootDialogOpen, setRebootDialogOpen] = useState(false);
  const [forceRebootDialogOpen, setForceRebootDialogOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<'1h' | '1d' | '1w'>('1h');
  const [isRebooting, setIsRebooting] = useState(false);
  const [isForceRebooting, setIsForceRebooting] = useState(false);
  const rebootCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const forceRebootCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch server metrics
  const shouldFetchMetrics = server.status.toLowerCase() === 'ready' && Boolean(server.ip_address);
  
  const {
    data: metricsData,
    isFetching: isMetricsFetching,
    isPending: isMetricsPending,
    refetch: refetchMetrics,
  } = useQuery<ServerMetricsResponse>({
    queryKey: ['server-metrics', server.id],
    queryFn: () => fetchServerMetrics(server.id),
    enabled: shouldFetchMetrics,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });

  const {
    data: historyData,
    isFetching: isHistoryFetching,
    refetch: refetchHistory,
  } = useQuery<ServerMetricsHistoryResponse>({
    queryKey: ['server-metrics-history', server.id, timeRange],
    queryFn: () => fetchServerMetricsHistory(server.id, timeRange),
    enabled: shouldFetchMetrics,
    staleTime: 30000,
  });

  const handleRefreshMetrics = useCallback(() => {
    refetchMetrics();
    refetchHistory();
  }, [refetchMetrics, refetchHistory]);

  // Derived metrics
  const metrics = metricsData?.metrics;
  const cpuPercentage = metrics?.cpu_usage ?? null;
  const cpuCores = metrics?.cpu_cores ?? null;
  const memoryUsedGb = metrics?.memory_used ?? null;
  const memoryTotalGb = metrics?.memory_total ?? null;
  const memoryPercentage = memoryUsedGb && memoryTotalGb ? Math.round((memoryUsedGb / memoryTotalGb) * 100) : null;
  const diskUsedGb = metrics?.disk_used ?? null;
  const diskTotalGb = metrics?.disk_total ?? null;
  const diskPercentage = diskUsedGb && diskTotalGb ? Math.round((diskUsedGb / diskTotalGb) * 100) : null;
  const uptime = metrics?.uptime_formatted ?? null;
  const hasLiveMetrics = metrics !== undefined && metrics !== null && (cpuPercentage !== null || memoryUsedGb !== null);
  const onlineStatus: 'loading' | 'online' | 'offline' =
    isMetricsPending && !metrics ? 'loading' : hasLiveMetrics ? 'online' : 'offline';
  const isMetricsBusy = isMetricsFetching || isHistoryFetching;

  useEffect(() => {
    return () => {
      if (rebootCooldownRef.current) clearTimeout(rebootCooldownRef.current);
      if (forceRebootCooldownRef.current) clearTimeout(forceRebootCooldownRef.current);
    };
  }, []);

  const handleSaveDisplayName = async () => {
    if (editedName.trim() === '') return;
    setIsUpdating(true);
    try {
      await onUpdateDisplayName(server.id, editedName);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddSSHKey = async () => {
    if (!sshKey.trim()) return;
    setIsAddingKey(true);
    try {
      await onAddSSHKey(server.id, sshKey);
      setSSHKey('');
      setSSHDialogOpen(false);
    } finally {
      setIsAddingKey(false);
    }
  };

  const handleRemoveServer = async () => {
    if (confirmName !== server.server_name) return;
    setIsRemoving(true);
    try {
      await onDestroy(server.id);
      setRemoveDialogOpen(false);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRebootServer = async () => {
    setIsRebooting(true);
    try {
      await onReboot(server.id);
      toast.success('Reboot started', {
        description: `${server.display_name || server.server_name} is rebooting. This may take a minute.`,
      });
      if (rebootCooldownRef.current) clearTimeout(rebootCooldownRef.current);
      rebootCooldownRef.current = setTimeout(() => {
        setIsRebooting(false);
        rebootCooldownRef.current = null;
      }, 30000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reboot server';
      alert(message);
      setIsRebooting(false);
    }
  };

  const handleForceRebootServer = async () => {
    setIsForceRebooting(true);
    try {
      await onForceReboot(server.id);
      toast.success('Force reboot started', {
        description: `${server.display_name || server.server_name} is being hard reset.`,
      });
      if (forceRebootCooldownRef.current) clearTimeout(forceRebootCooldownRef.current);
      forceRebootCooldownRef.current = setTimeout(() => {
        setIsForceRebooting(false);
        forceRebootCooldownRef.current = null;
      }, 30000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to force reboot server';
      alert(message);
      setIsForceRebooting(false);
    }
  };

  const disablePowerActions = isRebooting || isForceRebooting;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'provisioning':
        return 'bg-green-500 animate-pulse';
      case 'ready':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card className="h-full">
      <CardContent>
        {/* Header with name and SSH key button */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-auto"
                  autoFocus
                />
                <Button size="sm" onClick={handleSaveDisplayName} disabled={isUpdating}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <h3 className="text-lg font-semibold">{server.display_name}</h3>
              </>
            )}
          </div>

          <Dialog open={sshDialogOpen} onOpenChange={setSSHDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="default">
                <Key className="h-4 w-4 mr-2" />
                Add SSH Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add SSH Key</DialogTitle>
                <DialogDescription>
                  Add a public SSH key to access this server.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="ssh-key">Public SSH Key</Label>
                  <Textarea
                    id="ssh-key"
                    value={sshKey}
                    onChange={(e) => setSSHKey(e.target.value)}
                    placeholder="ssh-rsa AAAAB3..."
                    rows={4}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Paste your public SSH key here (e.g., <code>ssh-rsa AAAAB3...</code>).
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSSHDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddSSHKey} disabled={isAddingKey || !sshKey.trim()}>
                  {isAddingKey ? 'Adding...' : 'Add SSH Key'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-muted-foreground">Status:</span>
          <span className="capitalize">{server.status}</span>
          <span className={cn('w-3 h-3 rounded-full', getStatusColor(server.status))} />
        </div>

        {/* IP Address */}
        <p className="text-muted-foreground mb-2">
          IP Address: {server.ip_address || <span className="text-gray-400">Pending...</span>}
        </p>

        {/* Odoo Instances */}
        <p className="text-muted-foreground mb-4">
          Odoo Instances: {server.active_instances_count}
        </p>

        {/* Security Updates Toggle */}
        <div className="flex items-center gap-3 mb-4">
          <Switch
            checked={server.automatic_security_updates}
            onCheckedChange={(checked) => onToggleSecurityUpdates(server.id, checked)}
          />
          <Badge variant={server.automatic_security_updates ? 'default' : 'secondary'}>
            {server.automatic_security_updates ? (
              <ShieldCheck className="h-3 w-3 mr-1" />
            ) : (
              <Shield className="h-3 w-3 mr-1" />
            )}
            Automatic OS & Security Updates
          </Badge>
        </div>

        {/* Server Metrics */}
        {shouldFetchMetrics && (
          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <ServerIcon className="h-4 w-4" />
                Server Metrics
                {isMetricsBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              </h4>
              <Button variant="ghost" size="sm" onClick={handleRefreshMetrics} disabled={isMetricsBusy}>
                <RefreshCw className={cn("h-4 w-4", isMetricsBusy && "animate-spin")} />
              </Button>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {/* CPU */}
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase font-medium mb-1">CPU</div>
                <div className="text-xl font-bold">{cpuPercentage !== null ? `${cpuPercentage}%` : '--'}</div>
                <div className="text-xs text-muted-foreground">{cpuCores !== null ? `${cpuCores} cores` : '-- cores'}</div>
                <Progress value={cpuPercentage ?? 0} className="h-1 mt-2" />
              </div>

              {/* Memory */}
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase font-medium mb-1">Memory</div>
                <div className="text-xl font-bold">{memoryUsedGb !== null ? `${memoryUsedGb.toFixed(1)}GB` : '--'}</div>
                <div className="text-xs text-muted-foreground">{memoryTotalGb !== null ? `${memoryTotalGb.toFixed(1)} GB total` : '-- GB total'}</div>
                <Progress value={memoryPercentage ?? 0} className="h-1 mt-2" />
              </div>

              {/* Disk */}
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase font-medium mb-1">Disk</div>
                <div className="text-xl font-bold">{diskUsedGb !== null ? `${diskUsedGb.toFixed(1)}GB` : '--'}</div>
                <div className="text-xs text-muted-foreground">{diskTotalGb !== null ? `${diskTotalGb.toFixed(1)} GB total` : '-- GB total'}</div>
                <Progress value={diskPercentage ?? 0} className="h-1 mt-2" />
              </div>
            </div>

            {/* CPU Usage Chart */}
            <div className="bg-white dark:bg-muted/30 border rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h6 className="text-sm font-medium mb-0.5">CPU Usage Over Time</h6>
                  <span className="text-xs text-muted-foreground">
                    {timeRange === '1h' ? 'Last hour' : timeRange === '1d' ? 'Last 24 hours' : 'Last 7 days'}
                  </span>
                </div>
                <div className="flex gap-1">
                  {(['1h', '1d', '1w'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={cn(
                        "px-2 py-1 text-xs rounded border",
                        timeRange === range 
                          ? "bg-primary text-primary-foreground border-primary" 
                          : "bg-background hover:bg-muted border-input"
                      )}
                    >
                      {range === '1h' ? '1H' : range === '1d' ? '1D' : '1W'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ width: '100%', height: 150 }}>
                {isHistoryFetching ? (
                  <div style={{ height: 150 }} className="flex items-center justify-center text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading chart data...
                  </div>
                ) : historyData?.history && historyData.history.cpu.length > 0 ? (
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart
                      data={historyData.history.cpu.map((value: number, index: number) => ({
                        time: historyData.history.timestamps?.[index] || index.toString(),
                        cpu: value,
                      }))}
                      margin={{ top: 10, right: 10, left: 5, bottom: 5 }}
                    >
                      <XAxis 
                        dataKey="time" 
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        tickLine={{ stroke: '#e5e7eb' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        interval="preserveStartEnd"
                        minTickGap={30}
                      />
                      <YAxis 
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        tickLine={{ stroke: '#e5e7eb' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={(value) => `${value}%`}
                        width={45}
                      />
                      <Tooltip 
                        formatter={(value: number) => [`${value.toFixed(1)}%`, 'CPU']}
                        contentStyle={{ 
                          fontSize: '12px',
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="cpu" 
                        stroke="#007bff"
                        fill="rgba(0, 123, 255, 0.1)"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4, fill: '#007bff' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 150 }} className="flex items-center justify-center text-muted-foreground text-sm">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    No data available
                  </div>
                )}
              </div>
            </div>

            {/* System Info */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-muted/50 rounded-md px-3 py-2 text-sm text-center">
                <Clock className="h-4 w-4 inline mr-2 text-muted-foreground" />
                {uptime ?? '--'}
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2 text-sm text-center">
                <ServerIcon className="h-4 w-4 inline mr-2 text-muted-foreground" />
                {onlineStatus === 'loading' ? (
                  <span className="text-muted-foreground inline-flex items-center gap-1">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span className={onlineStatus === 'online' ? 'text-green-600' : 'text-red-600'}>
                    {onlineStatus === 'online' ? 'Online' : 'Offline'}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-center gap-2 border-t pt-4">
        {server.provider === 'doploy' && (
          <>
            <Dialog open={rebootDialogOpen} onOpenChange={setRebootDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                  onClick={() => setRebootDialogOpen(true)}
                  disabled={disablePowerActions}
                >
                  {isRebooting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Rebooting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reboot
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reboot server</DialogTitle>
                  <DialogDescription>
                    Rebooting will temporarily interrupt all services on{' '}
                    <strong>{server.display_name || server.server_name}</strong>. Continue?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRebootDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      setRebootDialogOpen(false);
                      await handleRebootServer();
                    }}
                    disabled={isRebooting}
                  >
                    {isRebooting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Rebooting...
                      </>
                    ) : (
                      'Confirm Reboot'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={forceRebootDialogOpen} onOpenChange={setForceRebootDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => setForceRebootDialogOpen(true)}
                  disabled={disablePowerActions}
                >
                  {isForceRebooting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Force Rebooting...
                    </>
                  ) : (
                    <>
                      <Power className="h-4 w-4 mr-1" />
                      Force Reboot
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Force reboot server</DialogTitle>
                  <DialogDescription>
                    This is a hard reset and can cause data corruption. Only use if the server is unresponsive.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForceRebootDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      setForceRebootDialogOpen(false);
                      await handleForceRebootServer();
                    }}
                    disabled={isForceRebooting}
                  >
                    {isForceRebooting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Force Rebooting...
                      </>
                    ) : (
                      'Confirm Force Reboot'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="destructive" size="sm">
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Removal</DialogTitle>
            </DialogHeader>
            
            {server.active_instances_count > 0 ? (
              <Alert variant="destructive">
                <AlertTitle>Cannot Remove Server</AlertTitle>
                <AlertDescription>
                  This server has <strong>{server.active_instances_count}</strong> active Odoo instances.
                  Please remove these instances before deleting the server.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {server.provider === 'doploy' && server.billing_enabled && (
                  <Alert>
                    <AlertTitle>Billing Information</AlertTitle>
                    <AlertDescription>
                      <p>Removing this server will:</p>
                      <ul className="list-disc list-inside mt-2">
                        <li><strong>Immediately cancel this server's billing subscription</strong></li>
                        <li><strong>Permanently delete the server</strong> and all its data</li>
                      </ul>
                      {server.monthly_cost && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Monthly cost: ${server.monthly_cost.toFixed(2)}
                        </p>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                <Alert variant="destructive">
                  <AlertTitle>Permanent Deletion</AlertTitle>
                  <AlertDescription>
                    This action <strong>cannot be undone</strong>. All data on this server will be permanently lost.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <p>
                    Are you sure you want to remove the server <strong>{server.server_name}</strong>?
                  </p>
                  <p>Type the server name <strong>{server.server_name}</strong> below to confirm:</p>
                  <Input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder="Type server name"
                  />
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveServer}
                disabled={
                  server.active_instances_count > 0 ||
                  confirmName !== server.server_name ||
                  isRemoving
                }
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
}
